// ---------------------------------------------------------------------------
// Error classes
// ---------------------------------------------------------------------------

export class CopeError extends Error {
  constructor(message: string) {
    super(message)
    this.name = 'CopeError'
  }
}

export class CopeApiError extends CopeError {
  readonly code: ApiErrorCode
  readonly status: number
  readonly field?: string
  readonly requestId: string | null
  readonly errors: ReadonlyArray<{ readonly code: string; readonly message: string; readonly field?: string }>

  constructor(
    status: number,
    errors: Array<{ code: string; message: string; field?: string }>,
    requestId?: string | null,
  ) {
    const first = errors[0]
    super(first?.message ?? `API error ${status}`)
    this.name = 'CopeApiError'
    this.status = status
    this.code = (first?.code ?? 'unknown') as ApiErrorCode
    this.field = first?.field
    this.requestId = requestId ?? null
    this.errors = errors
  }
}

export class CopeNetworkError extends CopeError {
  constructor(message: string) {
    super(message)
    this.name = 'CopeNetworkError'
  }
}

export class CopeCartExpiredError extends CopeError {
  readonly state: CartState

  constructor(state: CartState) {
    super(`Cart is ${state} and cannot be used`)
    this.name = 'CopeCartExpiredError'
    this.state = state
  }
}

// ---------------------------------------------------------------------------
// Error codes — known codes + string escape hatch
// ---------------------------------------------------------------------------

export type ApiErrorCode =
  | 'rate_limited'
  | 'cart_not_active'
  | 'duplicate_line'
  | 'not_saleable'
  | 'empty_cart'
  | 'incomplete_buyer_identity'
  | 'incomplete_shipping_address'
  | 'missing_consent'
  | 'below_minimum_charge'
  | 'invalid_redirect_url'
  | 'checkout_expired'
  | 'checkout_not_open'
  | 'checkout_not_cancellable'
  | 'invalid_promo_code'
  | 'missing_code'
  | 'missing_tax_location'
  | 'tax_service_unavailable'
  | 'payment_provider_error'
  | 'unauthorized'
  | 'not_found'
  | 'validation_error'
  | 'cart_id_mismatch'
  | (string & {}) // escape hatch for unknown codes

// ---------------------------------------------------------------------------
// Cart
// ---------------------------------------------------------------------------

export type CartState =
  | 'active'
  | 'checkout_in_progress'
  | 'ordered'
  | 'expired'
  | 'abandoned'
  | 'merged'

export interface Cart {
  readonly id: string
  readonly state: CartState
  readonly version: number
  readonly currency: string
  readonly charge_currency: string
  /** Present only in the `createCart()` response. The SDK persists it to localStorage automatically. */
  readonly cart_secret?: string
  readonly lines: readonly CartLine[]
  readonly buyer_identity: BuyerIdentity | null
  readonly totals: CartTotals | null
  readonly locale: string
  readonly promo_code: string | null
}

// ---------------------------------------------------------------------------
// Cart Line
// ---------------------------------------------------------------------------

export interface CartLine {
  readonly id: string
  readonly cart_id: string
  readonly product_id: number
  readonly plan_id: number | null
  readonly quantity: number
  readonly price_snapshot: PriceSnapshot | null
  readonly product_uuid: string
  readonly stale?: boolean
  readonly requires_shipping?: boolean
  readonly trial_days?: number
  readonly payment_methods: readonly string[]
  readonly plan_type: string | null
}

export interface PriceSnapshot {
  readonly unit_price_minor_units: number
  readonly currency: string
  readonly charge_currency: string
  readonly product_name: string
  readonly plan_name: string | null
  readonly captured_at: string
}

// ---------------------------------------------------------------------------
// Buyer Identity
// ---------------------------------------------------------------------------

export interface Address {
  readonly line1: string
  readonly line2: string | null
  readonly city: string
  readonly region?: string | null
  readonly postal_code: string
  readonly country: string
}

export interface BuyerIdentity {
  readonly email: string
  readonly country: string
  readonly postal_code: string | null
  readonly company: string | null
  readonly buyer_type: 'b2b' | 'b2c' | null
  readonly billing_address: Address | null
  readonly shipping_address: Address | null
  readonly consents: readonly Consent[]
}

export interface Consent {
  readonly type: string
  readonly granted_at: string
  readonly ip: string
}

// ---------------------------------------------------------------------------
// Totals
// ---------------------------------------------------------------------------

export type TotalsFinality = 'estimate' | 'final'

export interface CartTotals {
  readonly subtotal_minor_units: number
  readonly tax_minor_units: number
  readonly shipping_minor_units: number
  readonly discount_minor_units: number
  readonly total_minor_units: number
  /** Currency the *_minor_units are denominated in. Always equals `charge_currency`. */
  readonly currency: string
  /** Currency that will be billed (Stripe PaymentIntent.currency). */
  readonly charge_currency: string
  readonly finality: TotalsFinality
  /** Buyer's selected presentment currency, when different from `charge_currency`.
   *  Paired with `presentment_exchange_rate`; both present or both absent.
   *  To display amounts in presentment, multiply each `*_minor_units` by `presentment_exchange_rate`. */
  readonly presentment_currency?: string
  readonly presentment_exchange_rate?: number
}

// ---------------------------------------------------------------------------
// Product (read-only, for getProduct)
// ---------------------------------------------------------------------------

export interface SdkProduct {
  readonly uuid: string
  readonly name: string
  readonly headline: string | null
  readonly product_type: string
  readonly currency: string
  readonly payment_methods: readonly string[]
  readonly images: readonly SdkProductImage[]
  readonly payment_plans: readonly SdkPaymentPlan[]
}

export interface SdkProductImage {
  readonly id: number
  readonly image_url: string
}

export interface SdkPaymentPlan {
  readonly id: number
  readonly plan_type: 'one_time' | 'subscription' | 'installment'
  readonly position: number
  readonly currency: string
  readonly display_name: string | null
  /** Price as a decimal string, e.g. "24.99" (dollars, not cents despite the field name) */
  readonly first_payment_amount_cents: string
  readonly next_payments_amount_cents: string | null
  readonly next_payments_count: number | null
  readonly shipping_price_cents: string
  readonly charge_shipping_once: boolean
  readonly shipping_depend_on_quantity: boolean
  readonly interval: 'day' | 'week' | 'month' | 'year' | null
  readonly interval_count: number | null
  readonly second_payment_in: number | null
  readonly vanity_prices: readonly SdkVanityPrice[]
}

export interface SdkVanityPrice {
  readonly id: number
  readonly currency: string
  readonly first_payment_amount_cents: string
  readonly next_payments_amount_cents: string | null
}

// ---------------------------------------------------------------------------
// Checkout
// ---------------------------------------------------------------------------

export type CheckoutStatus = 'open' | 'completed' | 'expired' | 'cancelled'

export interface CheckoutCreateEmbedPolicy {
  readonly allowed: true
  readonly parent_origin: string
}

export interface CheckoutCreateResponse {
  readonly checkout_id: string
  readonly checkout_url: string
  readonly embed?: CheckoutCreateEmbedPolicy
  readonly status: CheckoutStatus
  readonly expires_at: string
  readonly totals: CartTotals
  readonly cart_version: number
}

// ---------------------------------------------------------------------------
// SDK Options & Payloads
// ---------------------------------------------------------------------------

export interface CopeCartOptions {
  readonly publishableKey: string
  readonly baseUrl?: string
  readonly checkoutBaseUrl?: string
}

export interface CreateCartPayload {
  readonly currency: string
  readonly locale?: string
  readonly attribution?: CartAttribution
  readonly metadata?: CartMetadata
}

export interface CartAttribution {
  readonly affiliate_id?: string
  readonly promo_code?: string
  readonly utm_source?: string
  readonly utm_medium?: string
  readonly utm_campaign?: string
  readonly utm_content?: string
  readonly utm_term?: string
  readonly landing_url?: string
  readonly referrer?: string
  readonly captured_at?: string
}

export type CartMetadataValue =
  | string
  | number
  | boolean
  | null
  | readonly CartMetadataValue[]
  | { readonly [key: string]: CartMetadataValue }

export interface CartMetadata {
  readonly [key: string]: CartMetadataValue
}

export interface AddLinePayload {
  /** Product UUID string — resolved to integer FK in API response (CartLine.product_id) */
  readonly product_id: string
  /** Optional payment plan ID. Omit it to let the API select the product's default plan. */
  readonly plan_id?: number
  readonly quantity?: number
}

export interface UpdateLinePayload {
  readonly quantity?: number
  readonly plan_id?: number
}

export interface UpdateBuyerIdentityPayload {
  readonly email?: string
  readonly first_name?: string
  readonly last_name?: string
  readonly country?: string
  readonly postal_code?: string
  readonly company?: string
  readonly buyer_type?: 'b2b' | 'b2c'
  readonly vat_id?: string
  readonly billing_address?: Partial<Address>
  readonly shipping_address?: Partial<Address>
  readonly consents?: ReadonlyArray<{ readonly type: string }>
}

export interface CheckoutPayload {
  readonly success_url?: string
  readonly cancel_url?: string
  readonly embed_origin?: string
  readonly consents: ReadonlyArray<{ readonly type: string; readonly version?: string }>
}

export interface CheckoutResult {
  readonly checkoutId: string
  readonly checkoutUrl: string
  readonly embedCheckoutUrl: string
  readonly embedOrigin?: string
  readonly status: CheckoutStatus
  readonly expiresAt: string
  readonly totals: CartTotals
}

export interface PrefilledCheckoutLineInput {
  readonly productId: string
  /** Optional payment plan ID. Omit it to let the API select the product's default plan. */
  readonly planId?: number
  readonly quantity?: number
}

interface PrefilledCheckoutBaseInput {
  /**
   * Stable caller-side reference, e.g. an event sales session ID. The SDK uses
   * it for the backend Idempotency-Key and stores it as cart metadata. Keep it
   * unique within the first 120 sanitized characters because that prefix is the
   * stable idempotency key input.
   */
  readonly externalReference: string
  readonly currency: string
  readonly locale?: string
  readonly attribution?: CartAttribution
  /**
   * Non-sensitive reconciliation metadata. `external_reference` and
   * `intended_payment_method` are reserved metadata keys populated from the
   * matching top-level SDK fields.
   */
  readonly metadata?: CartMetadata
  readonly intendedPaymentMethod?: string
  readonly buyerIdentity: UpdateBuyerIdentityPayload
  readonly checkout: CheckoutPayload
}

export type PrefilledCheckoutInput = PrefilledCheckoutBaseInput & (
  | {
    readonly line: PrefilledCheckoutLineInput
    readonly lines?: never
  }
  | {
    readonly line?: never
    readonly lines: readonly [PrefilledCheckoutLineInput, ...PrefilledCheckoutLineInput[]]
  }
)

export interface PrefilledCheckoutResult {
  /**
   * Cart ID for logging and reconciliation only. The prefilled handoff response
   * intentionally excludes `cart_secret`, so this SDK instance cannot use this
   * cart ID for follow-up mutations such as `addLine`, `reprice`, or `checkout`.
   */
  readonly cartId: string
  readonly cartVersion: number
  readonly checkoutId: string
  readonly checkoutToken: string
  readonly checkoutUrl: string
  readonly embedCheckoutUrl: string
  readonly embedOrigin?: string
  readonly status: CheckoutStatus
  readonly expiresAt: string
  readonly totals: CartTotals
  readonly intendedPaymentMethod?: string
}

export type CheckoutEmbedErrorCode = 'not_found' | 'load_failed' | 'embed_not_allowed' | 'confirm_failed'
export type CheckoutEmbedFallbackMode = 'redirect' | 'error'

export interface CheckoutEmbedErrorPayload {
  readonly code: CheckoutEmbedErrorCode
  readonly retryable: boolean
}

export interface CheckoutEmbedTerminalPayload {
  readonly status: 'expired' | 'completed' | 'unavailable' | 'cancelled' | 'processing'
}

export interface CheckoutEmbedResizePayload {
  readonly height: number
}

export interface CheckoutEmbedEventMetadata {
  readonly messageId?: string
  readonly timestamp?: string
}

export type CheckoutEmbedEvent =
  | ({ readonly type: 'ready' } & CheckoutEmbedEventMetadata)
  | ({ readonly type: 'error'; readonly payload: CheckoutEmbedErrorPayload } & CheckoutEmbedEventMetadata)
  | ({ readonly type: 'terminal'; readonly payload: CheckoutEmbedTerminalPayload } & CheckoutEmbedEventMetadata)
  | ({ readonly type: 'resize'; readonly payload: CheckoutEmbedResizePayload } & CheckoutEmbedEventMetadata)

export interface CheckoutMountMessage {
  readonly type: 'copecart.checkout.mount'
  readonly version: 1
  readonly token: string
}

export interface MountCheckoutOptions {
  readonly target: HTMLElement | string
  readonly title?: string
  readonly className?: string
  /**
   * What to do when the iframe never completes the trusted ready handshake.
   * Defaults to `error`; set `redirect` for a buyer-safe hosted checkout fallback.
   */
  readonly fallback?: CheckoutEmbedFallbackMode
  readonly readyTimeoutMs?: number
  /**
   * Called after a trusted `cope.checkout` ready message from the mounted checkout iframe.
   * `mountCheckout()` intentionally does not expose checkout tokens, cart secrets,
   * payment client secrets, or buyer PII through any callback payload.
   */
  readonly onReady?: () => void
  readonly onResize?: (payload: CheckoutEmbedResizePayload) => void
  readonly onError?: (payload: CheckoutEmbedErrorPayload) => void
  readonly onTerminal?: (payload: CheckoutEmbedTerminalPayload) => void
  readonly onSuccess?: () => void
  readonly onCancel?: () => void
  readonly onFallbackRedirect?: () => void
  readonly onMessage?: (event: CheckoutEmbedEvent) => void
}

export type MountCheckoutCallbacks = Omit<MountCheckoutOptions, 'target'>

export interface MountedCheckout {
  /**
   * Hosted checkout iframe. The SDK sets `allow="payment *"` and
   * `referrerPolicy="no-referrer"`.
   *
   * The SDK intentionally omits a `sandbox` attribute: no sandbox token set has
   * been proven compatible with all required wallet, redirect, and payment
   * provider flows yet. Embedding is instead constrained by the registered
   * `embed_origin` contract plus strict postMessage origin/source checks.
   */
  readonly iframe: HTMLIFrameElement
  destroy(): void
}
